import { useEffect, useState } from 'react'
import React from 'react'
import { Container, Card, Row, Col, Button } from 'react-bootstrap'
import { 
  FaArrowLeft, 
  FaUser, 
  FaPhone, 
  FaEnvelope, 
  FaMapMarkerAlt,
  FaChalkboardTeacher
} from 'react-icons/fa'
import { 
  BsPrinterFill,
  BsPencilFill,
  BsBuilding,
  BsCalendar3,
  BsPersonBadge,
  BsFileEarmarkText,
  BsChevronDown,
  BsChevronUp,
  BsFileEarmarkPdfFill
} from 'react-icons/bs'
import { useLocation, useNavigate, useParams } from 'react-router-dom'
import ApiEndpoints, {BASE_URL} from './commonComponents/ApiEndpoints'
import Cookies from 'js-cookie'
import toast from 'react-hot-toast'
import { useSidebar } from './SidebarContext'
import { useAuth } from './AuthContext'
import ProfileDropdown from './commonComponents/ProfileDropdown'
import { LuGraduationCap } from 'react-icons/lu'
import jsPDF from 'jspdf'
import { useInstitute } from './commonComponents/InstituteContext'

const TeacherProfile = () => {
  const { logout } = useAuth()
  const { isCollapsed } = useSidebar()
  const [marginLeft, setMarginLeft] = useState('0px')
  const [isMobile, setIsMobile] = useState(
    typeof window !== 'undefined' ? window.innerWidth <= 768 : false,
  )
  const [logoError, setLogoError] = useState(false)

  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth >= 992) {
        const expandedWidth = 245
        const collapsedWidth = 85
        const gap = 1

        setMarginLeft(
          isCollapsed
            ? `${collapsedWidth + gap}px`
            : `${expandedWidth + gap}px`,
        )
      } else {
        setMarginLeft('0px')
      }

      setIsMobile(window.innerWidth <= 768)
    }

    handleResize()
    window.addEventListener('resize', handleResize)
    return () => window.removeEventListener('resize', handleResize)
  }, [isCollapsed])

  const [institutes, setInstitutes] = useState([])
  const navigate = useNavigate()
  const { logo } = useInstitute()
  const { id } = useParams()
  const location = useLocation()
  const [teacher, setTeacher] = useState(location.state?.teacher || null)
  const fromLeftTeachers = Boolean(location.state?.fromLeftTeachers)

  // Section visibility states - only Personal Information open initially
  const [showPersonalInfo, setShowPersonalInfo] = useState(true)
  const [showProfessionalInfo, setShowProfessionalInfo] = useState(false)

  useEffect(() => {
    const institueId = Cookies.get('institute_id')

    if (!teacher && id && institueId) {
      ApiEndpoints.getAllTeachers({ 'institute-Id': Number(institueId) })
        .then((res) => {
          if (Array.isArray(res.data.data)) {
            const matchedTeacher = res.data.data.find(
              (t) => String(t.id) === String(id),
            )
            if (matchedTeacher) {
              setTeacher(matchedTeacher)
            } else {
              console.error('No teacher found with ID:', id)
            }
          }
        })
        .catch((err) => {
          console.error('Failed to fetch teachers:', err)
        })
    }
  }, [id, teacher])

  useEffect(() => {
    ApiEndpoints.getAllInstitutes().then((res) => {
      if (Array.isArray(res.data.data)) {
        setInstitutes(res.data.data)
      } else {
        setInstitutes([])
      }
    })
  }, [])

  useEffect(() => {
    const storedInstitute = Cookies.get('institute_name')
    if (storedInstitute) {
      setInstitutes([{ name: storedInstitute }])
    }
  }, [])

  const calculateAge = (dob) => {
    if (!dob) return ''
    const birthDate = new Date(dob)
    const today = new Date()
    let age = today.getFullYear() - birthDate.getFullYear()
    const monthDiff = today.getMonth() - birthDate.getMonth()

    if (
      monthDiff < 0 ||
      (monthDiff === 0 && today.getDate() < birthDate.getDate())
    ) {
      age--
    }
    return age
  }

  const handleBack = () => {
    navigate(fromLeftTeachers ? '/LeftTeachers' : '/TeacherList')
  }

  const handleUpdateTeacherProfile = () => {
    navigate(`/UpdateTeacherProfile/${id}`, { state: { teacher } })
  }

  const handleIdCard = () => {
    navigate(`/TeacherIdcard/${id}`, { state: { teacher } })
  }

  const handleSaveAsPDF = () => {
    if (!teacher) {
      toast.error('Teacher data not available')
      return
    }

    try {
      const doc = new jsPDF('p', 'mm', 'a4')
      const pageWidth = doc.internal.pageSize.getWidth()
      const pageHeight = doc.internal.pageSize.getHeight()
      const margin = 15
      let yPos = margin

      const checkPageBreak = (requiredHeight) => {
        if (yPos + requiredHeight > pageHeight - margin) {
          doc.addPage()
          yPos = margin
        }
      }

      // Title
      doc.setFontSize(20)
      doc.setFont('helvetica', 'bold')
      doc.setTextColor(37, 99, 235)
      doc.text('Teacher Information', pageWidth / 2, yPos, { align: 'center' })
      yPos += 10

      // Teacher Name
      doc.setFontSize(18)
      doc.setFont('helvetica', 'bold')
      doc.setTextColor(17, 24, 39)
      const teacherName = teacher?.name
        ? teacher.name.charAt(0).toUpperCase() + teacher.name.slice(1).toLowerCase()
        : 'N/A'
      doc.text(teacherName, pageWidth / 2, yPos, { align: 'center' })
      yPos += 8

      // Email
      doc.setFontSize(12)
      doc.setFont('helvetica', 'normal')
      doc.setTextColor(107, 114, 128)
      doc.text(`Email: ${teacher?.email || 'N/A'}`, pageWidth / 2, yPos, { align: 'center' })
      yPos += 10

      // Divider
      doc.setDrawColor(209, 213, 219)
      doc.line(margin, yPos, pageWidth - margin, yPos)
      yPos += 8

      // Personal Information Section
      doc.setFontSize(14)
      doc.setFont('helvetica', 'bold')
      doc.setTextColor(37, 99, 235)
      doc.text('Personal Information', margin, yPos)
      yPos += 8

      doc.setFontSize(10)
      doc.setFont('helvetica', 'normal')
      doc.setTextColor(17, 24, 39)

      const personalDetails = [
        `Gender: ${teacher?.gender || 'N/A'}`,
        `Birth Date: ${teacher?.dob || 'N/A'}`,
        `Age: ${calculateAge(teacher?.dob) || 'N/A'} years`,
        `Phone Number: ${teacher?.phone || 'N/A'}`,
        `Address: ${teacher?.address || 'N/A'}`
      ]

      personalDetails.forEach((detail) => {
        checkPageBreak(6)
        doc.text(detail, margin + 5, yPos)
        yPos += 6
      })
      yPos += 10

      // Professional Information Section
      checkPageBreak(20)
      doc.setDrawColor(209, 213, 219)
      doc.line(margin, yPos, pageWidth - margin, yPos)
      yPos += 8

      doc.setFontSize(14)
      doc.setFont('helvetica', 'bold')
      doc.setTextColor(16, 185, 129)
      doc.text('Professional Information', margin, yPos)
      yPos += 8

      doc.setFontSize(10)
      doc.setFont('helvetica', 'normal')
      doc.setTextColor(17, 24, 39)

      const instituteName = teacher?.institute_id
        ? institutes.find((i) => i.institute_id === teacher.institute_id)?.name || 'N/A'
        : 'N/A'

      const professionalDetails = [
        `Institute Name: ${instituteName}`,
        `Qualification: ${teacher?.qualification || 'N/A'}`,
        `Joining Date: ${teacher?.joining_date || 'N/A'}`,
        `Status: ${teacher?.status ? teacher.status.charAt(0).toUpperCase() + teacher.status.slice(1) : 'N/A'}`
      ]

      professionalDetails.forEach((detail) => {
        checkPageBreak(6)
        doc.text(detail, margin + 5, yPos)
        yPos += 6
      })

      // Footer
      const totalPages = doc.internal.pages.length - 1
      for (let i = 1; i <= totalPages; i++) {
        doc.setPage(i)
        doc.setFontSize(8)
        doc.setFont('helvetica', 'italic')
        doc.setTextColor(107, 114, 128)
        doc.text(
          `Generated on: ${new Date().toLocaleDateString()} | Page ${i} of ${totalPages}`,
          pageWidth / 2,
          pageHeight - 10,
          { align: 'center' }
        )
      }

      const fileName = `Teacher_Profile_${teacher?.name || 'Teacher'}_${new Date().getTime()}.pdf`
      doc.save(fileName)
      toast.success('PDF generated successfully!')
    } catch (error) {
      console.error('Error generating PDF:', error)
      toast.error('Failed to generate PDF. Please try again.')
    }
  }

  const instituteName = teacher?.institute_id
    ? institutes.find((i) => i.institute_id === teacher.institute_id)?.name || 'N/A'
    : 'N/A'

  const defaultTeacherPhoto = `${process.env.PUBLIC_URL || ''}/student.png`
  const teacherProfileImage = (() => {
    const img = teacher?.teacher_image
    if (
      img &&
      String(img).trim() !== '' &&
      String(img).toLowerCase() !== 'null'
    ) {
      return img
    }
    return defaultTeacherPhoto
  })()

  if (!teacher) {
    return (
      <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '50vh' }}>
        <p style={{ color: '#6b7280', fontSize: '16px', fontFamily: 'Inter, system-ui, sans-serif' }}>
          Loading teacher data...
        </p>
      </div>
    )
  }

  return (
    <>
      <style>
        {`
          .info-label {
            font-size: 12px;
            font-weight: 600;
            color: #6b7280;
            text-transform: uppercase;
            letter-spacing: 0.5px;
            margin-bottom: 6px;
            font-family: 'Inter', system-ui, sans-serif;
          }
          .info-value {
            font-size: 15px;
            font-weight: 500;
            color: #111827;
            font-family: 'Inter', system-ui, sans-serif;
          }
          .action-button {
            transition: all 0.2s ease;
          }
          .action-button:hover {
            transform: translateY(-2px);
            box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
          }
        `}
      </style>

      <div 
        style={{ 
          marginLeft,
          transition: 'margin-left 0.35s cubic-bezier(0.4, 0, 0.2, 1)',
          willChange: 'margin-left',
          WebkitTransition: 'margin-left 0.35s cubic-bezier(0.4, 0, 0.2, 1)',
          position: 'relative',
          zIndex: 1,
          marginTop: '0px',
          paddingLeft: isCollapsed ? '12px' : '12px',
          paddingRight: isCollapsed ? '12px' : '12px',
        }}
      >
        {/* Header */}
          <div
            className="container-fluid p-0"
            style={{
              marginTop: window.innerWidth < 992 ? '55px' : '-12px',
            paddingLeft: '0px',
            paddingRight: '0px',
          }}
        >
          <div style={{ marginLeft: isMobile ? '-13px' : '-40px' }}>
            <div 
              className="d-flex justify-content-between align-items-center"
              style={{
                background: '#F3F4F6',
                paddingLeft: '28px',
                paddingRight: '28px',
                paddingTop: '12px',
                paddingBottom: '12px',
                overflow: 'visible',
                borderBottom: '1px solid #D1D3D4',
                width: '100%',
              }}
            >
              {/* Left: Title */}
              <div className="d-flex align-items-center gap-3">
                {/* School Logo */}
                <div style={{ 
                  width: '40px', 
                  height: '40px', 
                  borderRadius: '50%', 
                  overflow: 'hidden', 
                  background: '#223843',
                  display: 'flex', 
                  alignItems: 'center', 
                  justifyContent: 'center', 
                  flexShrink: 0,
                }}>
                  {!logoError ? (
                    <img 
                      src={logo} 
                      alt="School Logo" 
                      style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                      onError={() => setLogoError(true)}
                    />
                  ) : (
                    <LuGraduationCap size={20} style={{ color: '#ffffff' }} />
                  )}
                </div>
                
                <h3 
                  className="m-0 fw-bold" 
                  style={{ 
                    letterSpacing: '-0.02em',
                    overflow: 'visible',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                    fontSize: 'clamp(18px, 2.5vw, 22px)',
                    color: '#111827',
                    lineHeight: '1.2',
                    fontFamily: 'Inter, system-ui, sans-serif',
                  }}
                >
                  {teacher?.name
                    ? teacher.name.charAt(0).toUpperCase() +
                      teacher.name.slice(1).toLowerCase()
                    : 'Teacher'} Profile
                </h3>
                {!fromLeftTeachers && (
                  <button
                    onClick={handleUpdateTeacherProfile}
                    style={{
                      width: '40px',
                      height: '40px',
                      borderRadius: '50%',
                      backgroundColor: '#223843',
                      border: 'none',
                      color: '#ffffff',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      cursor: 'pointer',
                      transition: 'all 0.2s ease',
                      flexShrink: 0,
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.backgroundColor = '#223843'
                      e.currentTarget.style.transform = 'scale(1.05)'
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.backgroundColor = '#223843'
                      e.currentTarget.style.transform = 'scale(1)'
                    }}
                    title="Update Profile"
                  >
                    <BsPencilFill size={20} />
                  </button>
                )}
              </div>
                
              {/* Right: Profile Icon with Dropdown */}
              {!isMobile && (
                <ProfileDropdown logout={logout} />
              )}
            </div>
          </div>
        </div>

        <Container 
          className="mt-4"
          style={{
            paddingLeft: isCollapsed ? '32px' : '40px',
            paddingRight: '32px',
            paddingTop: '32px',
            paddingBottom: '32px',
          }}
        >
          {/* Profile Summary Section - Two Column Layout */}
          <Row className="mb-4">
            {/* Profile Summary Card (Left) */}
            <Col xs={12} md={8}>
              <Card 
                style={{
                  border: '1px solid #D1D3D4',
                  borderRadius: '16px',
                  boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
                  height: '100%',
                }}
              >
                <Card.Body style={{ padding: '40px', textAlign: 'center' }}>
                  {/* Profile Photo with Back Button */}
                  <div className="mb-4" style={{ position: 'relative', display: 'inline-block' }}>
                    <div
                      style={{
                        width: '140px',
                        height: '140px',
                        borderRadius: '50%',
                        border: '4px solid #223843',
                        overflow: 'visible',
                        background: '#ffffff',
                        display: 'inline-block',
                        boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)',
                        position: 'relative',
                      }}
                    >
                      <img
                        src={teacherProfileImage}
                        alt="Profile"
                        style={{
                          width: '100%',
                          height: '100%',
                          objectFit: 'cover',
                          borderRadius: '50%',
                        }}
                      />
                      {/* Back to List Button */}
                      <button
                        onClick={handleBack}
                        style={{
                          position: 'absolute',
                          bottom: '-8px',
                          right: '-8px',
                          width: '40px',
                          height: '40px',
                          borderRadius: '50%',
                          backgroundColor: '#223843',
                          border: '3px solid #ffffff',
                          color: '#ffffff',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          cursor: 'pointer',
                          transition: 'all 0.2s ease',
                          boxShadow: '0 2px 4px rgba(0, 0, 0, 0.2)',
                          zIndex: 10,
                        }}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.backgroundColor = '#223843'
                          e.currentTarget.style.transform = 'scale(1.1)'
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.backgroundColor = '#223843'
                          e.currentTarget.style.transform = 'scale(1)'
                        }}
                        title="Back to Teacher list"
                      >
                        <FaArrowLeft size={16} />
                      </button>
                    </div>
                  </div>

                  {/* Teacher Name */}
                  <h2 className='text-uppercase'
                    style={{
                      color: '#111827',
                      fontWeight: '700',
                      fontSize: 'clamp(24px, 3vw, 32px)',
                      marginBottom: '12px',
                      fontFamily: 'Inter, system-ui, sans-serif',
                    }}
                  >
                    {teacher?.name
                      ? teacher.name.charAt(0).toUpperCase() +
                        teacher.name.slice(1).toLowerCase()
                      : 'N/A'}
                  </h2>

                  {/* Email */}
                  <div className="mb-4">
                    <p 
                      style={{
                        color: '#6b7280',
                        fontSize: '16px',
                        marginBottom: '8px',
                        fontFamily: 'Inter, system-ui, sans-serif',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: '8px',
                      }}
                    >
                      <FaEnvelope size={16} />
                      {teacher?.email || 'N/A'}
                    </p>
                  </div>

                  {/* Badges */}
                  <div className="d-flex flex-wrap justify-content-center gap-2">
                    <span
                      style={{
                        backgroundColor: '#EFF6FF',
                        color: '#223843',
                        padding: '8px 16px',
                        borderRadius: '20px',
                        fontSize: '13px',
                        fontWeight: '600',
                        fontFamily: 'Inter, system-ui, sans-serif',
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: '6px',
                      }}
                    >
                      <BsBuilding />
                      {instituteName}
                    </span>
                    <span
                      style={{
                        backgroundColor: teacher?.status === 'active' ? '#ECFDF5' : '#FEE2E2',
                        color: teacher?.status === 'active' ? '#10B981' : '#EF4444',
                        padding: '8px 16px',
                        borderRadius: '20px',
                        fontSize: '13px',
                        fontWeight: '600',
                        fontFamily: 'Inter, system-ui, sans-serif',
                      }}
                    >
                      {teacher?.status
                        ? teacher.status.charAt(0).toUpperCase() +
                          teacher.status.slice(1)
                        : 'N/A'}
                    </span>
                    {teacher?.qualification && (
                      <span
                        style={{
                          backgroundColor: '#EFF6FF',
                          color: '#223843',
                          padding: '8px 16px',
                          borderRadius: '20px',
                          fontSize: '13px',
                          fontWeight: '600',
                          fontFamily: 'Inter, system-ui, sans-serif',
                        }}
                      >
                        {teacher.qualification}
                      </span>
                    )}
                  </div>

                  {/* Save as PDF Button */}
                  <div className="mt-4 d-flex justify-content-center">
                    <button
                      onClick={handleSaveAsPDF}
                      style={{
                        backgroundColor: '#10B981',
                        border: 'none',
                        color: '#ffffff',
                        borderRadius: '12px',
                        padding: '12px 24px',
                        fontWeight: '600',
                        fontSize: '14px',
                        fontFamily: 'Inter, system-ui, sans-serif',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: '8px',
                        cursor: 'pointer',
                        transition: 'all 0.2s ease',
                        boxShadow: '0 2px 4px rgba(16, 185, 129, 0.2)',
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.backgroundColor = '#059669'
                        e.currentTarget.style.transform = 'translateY(-2px)'
                        e.currentTarget.style.boxShadow = '0 4px 8px rgba(16, 185, 129, 0.3)'
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.backgroundColor = '#10B981'
                        e.currentTarget.style.transform = 'translateY(0)'
                        e.currentTarget.style.boxShadow = '0 2px 4px rgba(16, 185, 129, 0.2)'
                      }}
                    >
                      <BsFileEarmarkPdfFill size={18} /> Save as PDF
                    </button>
                  </div>
                </Card.Body>
              </Card>
                  </Col>

            {/* Side Highlight Card (Right) */}
            <Col xs={12} md={4}>
              <Card 
                style={{
                  border: '1px solid #D1D3D4',
                  borderRadius: '16px',
                  boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
                  background: 'linear-gradient(135deg, #F3F4F6 0%, #ffffff 100%)',
                  height: '100%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  marginTop: isMobile ? '16px' : undefined,
                }}
              >
                <Card.Body style={{ 
                  padding: '40px', 
                  textAlign: 'center',
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  height: '100%',
                }}>
                  <div style={{ width: '100%' }}>
                    <div
                      style={{
                        width: '80px',
                        height: '80px',
                        borderRadius: '50%',
                        backgroundColor: '#EFF6FF',
                        display: 'inline-flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        marginBottom: '20px',
                      }}
                    >
                      <BsPrinterFill size={40} style={{ color: '#223843' }} />
                    </div>
                    <h5
                      style={{
                        color: '#111827',
                        fontWeight: '600',
                        fontSize: '18px',
                        marginBottom: '12px',
                        fontFamily: 'Inter, system-ui, sans-serif',
                      }}
                    >
                      Print ID Card
                    </h5>
                    <p
                      style={{
                        color: '#6b7280',
                        fontSize: '14px',
                        marginBottom: '24px',
                        fontFamily: 'Inter, system-ui, sans-serif',
                      }}
                    >
                      Generate and print teacher identification card
                    </p>
                    <Button
                      onClick={handleIdCard}
                      className="w-100 action-button"
                      style={{
                        backgroundColor: '#223843',
                        border: 'none',
                        color: '#ffffff',
                        borderRadius: '12px',
                        padding: '14px 28px',
                        fontWeight: '600',
                        fontSize: '16px',
                        fontFamily: 'Inter, system-ui, sans-serif',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: '8px',
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.backgroundColor = '#223843'
                        e.currentTarget.style.transform = 'translateY(-2px)'
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.backgroundColor = '#223843'
                        e.currentTarget.style.transform = 'translateY(0)'
                      }}
                    >
                      <BsPrinterFill /> Print ID Card
                    </Button>
                  </div>
                </Card.Body>
              </Card>
                  </Col>
                </Row>

          {/* Personal Information Section */}
          <Row className="mb-4">
            <Col xs={12}>
              <Card
                style={{
                  border: '1px solid #D1D3D4',
                  borderRadius: '16px',
                  boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.1)',
                  overflow: 'hidden',
                }}
              >
                {/* Header with Icon */}
                <div
                  style={{
                    background: 'linear-gradient(135deg, #223843 0%, #223843 100%)',
                    padding: '16px 24px',
                    borderBottom: '1px solid #D1D3D4',
                  }}
                >
                  <div className="d-flex align-items-center justify-content-between">
                    <div className="d-flex align-items-center gap-2">
                      <div
                        style={{
                          width: '36px',
                          height: '36px',
                          borderRadius: '10px',
                          backgroundColor: 'rgba(255, 255, 255, 0.2)',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          backdropFilter: 'blur(10px)',
                        }}
                      >
                        <FaUser size={18} style={{ color: '#ffffff' }} />
                      </div>
                      <div>
                        <h5 className='text-start'
                          style={{
                            margin: 0,
                            fontWeight: '700',
                            color: '#ffffff',
                            fontSize: '18px',
                            fontFamily: 'Inter, system-ui, sans-serif',
                          }}
                        >
                          Personal Information
                        </h5>
                        <p
                          style={{
                            margin: '2px 0 0 0',
                            color: 'rgba(255, 255, 255, 0.9)',
                            fontSize: '12px',
                            fontFamily: 'Inter, system-ui, sans-serif',
                          }}
                        >
                          Teacher personal details and contact information
                        </p>
                      </div>
                    </div>
                    <button
                      onClick={() => setShowPersonalInfo(!showPersonalInfo)}
                      style={{
                        width: '36px',
                        height: '36px',
                        borderRadius: '50%',
                        backgroundColor: 'rgba(255, 255, 255, 0.2)',
                        border: 'none',
                        color: '#ffffff',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        cursor: 'pointer',
                        transition: 'all 0.2s ease',
                        flexShrink: 0,
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.backgroundColor = 'rgba(255, 255, 255, 0.3)'
                        e.currentTarget.style.transform = 'scale(1.1)'
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.backgroundColor = 'rgba(255, 255, 255, 0.2)'
                        e.currentTarget.style.transform = 'scale(1)'
                      }}
                      title={showPersonalInfo ? "Hide Section" : "Show Section"}
                    >
                      {showPersonalInfo ? <BsChevronUp size={18} /> : <BsChevronDown size={18} />}
                    </button>
                  </div>
                </div>

                {showPersonalInfo && (
                  <Card.Body style={{ padding: '20px' }}>
                    <Row className="g-3 text-start">
                      {/* Left Column */}
                      <Col xs={12} md={6}>
                        {/* Personal Details Card */}
                        <div
                          style={{
                            backgroundColor: '#F9FAFB',
                            borderRadius: '12px',
                            padding: '14px',
                            border: '1px solid #E5E7EB',
                            marginBottom: '12px',
                          }}
                        >
                          <h6
                            style={{
                              marginBottom: '10px',
                              fontWeight: '600',
                              color: '#10B981',
                              fontSize: '12px',
                              textTransform: 'uppercase',
                              letterSpacing: '0.5px',
                              fontFamily: 'Inter, system-ui, sans-serif',
                            }}
                          >
                            Personal Details
                          </h6>
                          <div className="d-flex flex-column gap-2">
                            <div
                              style={{
                                backgroundColor: '#ffffff',
                                borderRadius: '8px',
                                padding: '8px 12px',
                                border: '1px solid #E5E7EB',
                              }}
                            >
                              <div className="d-flex align-items-center gap-2 mb-1">
                                <FaUser size={14} style={{ color: '#10B981' }} />
                                <div className="info-label" style={{ marginBottom: 0, fontSize: '11px' }}>Gender</div>
                              </div>
                              <div className="info-value" style={{ fontSize: '14px', fontWeight: '600', color: '#111827' }}>
                                {teacher?.gender || 'N/A'}
                              </div>
                            </div>

                            <div
                              style={{
                                backgroundColor: '#ffffff',
                                borderRadius: '8px',
                                padding: '8px 12px',
                                border: '1px solid #E5E7EB',
                              }}
                            >
                              <div className="d-flex align-items-center gap-2 mb-1">
                                <BsCalendar3 size={14} style={{ color: '#10B981' }} />
                                <div className="info-label" style={{ marginBottom: 0, fontSize: '11px' }}>Birth Date</div>
                              </div>
                              <div className="info-value" style={{ fontSize: '14px', fontWeight: '600', color: '#111827' }}>
                                {teacher?.dob || 'N/A'}
                              </div>
                            </div>

                            <div
                              style={{
                                backgroundColor: '#ffffff',
                                borderRadius: '8px',
                                padding: '8px 12px',
                                border: '1px solid #E5E7EB',
                              }}
                            >
                              <div className="d-flex align-items-center gap-2 mb-1">
                                <FaUser size={14} style={{ color: '#10B981' }} />
                                <div className="info-label" style={{ marginBottom: 0, fontSize: '11px' }}>Age</div>
                              </div>
                              <div className="info-value" style={{ fontSize: '14px', fontWeight: '600', color: '#111827' }}>
                                {calculateAge(teacher?.dob) || 'N/A'} years
                              </div>
                            </div>
                          </div>
                        </div>
                      </Col>

                      {/* Right Column */}
                      <Col xs={12} md={6}>
                        {/* Contact Information Card */}
                        <div
                          style={{
                            backgroundColor: '#F9FAFB',
                            borderRadius: '12px',
                            padding: '14px',
                            border: '1px solid #E5E7EB',
                          }}
                        >
                          <h6
                            style={{
                              marginBottom: '10px',
                              fontWeight: '600',
                              color: '#F59E0B',
                              fontSize: '12px',
                              textTransform: 'uppercase',
                              letterSpacing: '0.5px',
                              fontFamily: 'Inter, system-ui, sans-serif',
                            }}
                          >
                            Contact Information
                          </h6>
                          <div className="d-flex flex-column gap-2">
                            <div
                              style={{
                                backgroundColor: '#ffffff',
                                borderRadius: '8px',
                                padding: '8px 12px',
                                border: '1px solid #E5E7EB',
                              }}
                            >
                              <div className="d-flex align-items-center gap-2 mb-1">
                                <FaEnvelope size={14} style={{ color: '#F59E0B' }} />
                                <div className="info-label" style={{ marginBottom: 0, fontSize: '11px' }}>Email Address</div>
                              </div>
                              <div className="info-value" style={{ fontSize: '14px', fontWeight: '600', color: '#111827' }}>
                                {teacher?.email || 'N/A'}
                              </div>
                            </div>

                            <div
                              style={{
                                backgroundColor: '#ffffff',
                                borderRadius: '8px',
                                padding: '8px 12px',
                                border: '1px solid #E5E7EB',
                              }}
                            >
                              <div className="d-flex align-items-center gap-2 mb-1">
                                <FaPhone size={14} style={{ color: '#F59E0B' }} />
                                <div className="info-label" style={{ marginBottom: 0, fontSize: '11px' }}>Phone Number</div>
                              </div>
                              <div className="info-value" style={{ fontSize: '14px', fontWeight: '600', color: '#111827' }}>
                                {teacher?.phone || 'N/A'}
                              </div>
                            </div>

                            <div
                              style={{
                                backgroundColor: '#ffffff',
                                borderRadius: '8px',
                                padding: '8px 12px',
                                border: '1px solid #E5E7EB',
                              }}
                            >
                              <div className="d-flex align-items-center gap-2 mb-1">
                                <FaMapMarkerAlt size={14} style={{ color: '#F59E0B' }} />
                                <div className="info-label" style={{ marginBottom: 0, fontSize: '11px' }}>Address</div>
                              </div>
                              <div className="info-value" style={{ fontSize: '14px', fontWeight: '600', color: '#111827' }}>
                                {teacher?.address || 'N/A'}
                              </div>
                            </div>
                          </div>
                        </div>
                  </Col>
                    </Row>
                  </Card.Body>
                )}
              </Card>
                  </Col>
                </Row>

          {/* Professional Information Section */}
          <Row className="mb-4">
            <Col xs={12}>
              <Card
                style={{
                  border: '1px solid #D1D3D4',
                  borderRadius: '16px',
                  boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.1)',
                  overflow: 'hidden',
                }}
              >
                {/* Header with Icon */}
                <div
                  style={{
                    background: 'linear-gradient(135deg, #10B981 0%, #059669 100%)',
                    padding: '16px 24px',
                    borderBottom: '1px solid #D1D3D4',
                  }}
                >
                  <div className="d-flex align-items-center justify-content-between">
                    <div className="d-flex align-items-center gap-2">
                      <div
                        style={{
                          width: '36px',
                          height: '36px',
                          borderRadius: '10px',
                          backgroundColor: 'rgba(255, 255, 255, 0.2)',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          backdropFilter: 'blur(10px)',
                        }}
                      >
                        <FaChalkboardTeacher size={18} style={{ color: '#ffffff' }} />
                      </div>
                      <div>
                        <h5 className='text-start'
                          style={{
                            margin: 0,
                            fontWeight: '700',
                            color: '#ffffff',
                            fontSize: '18px',
                            fontFamily: 'Inter, system-ui, sans-serif',
                          }}
                        >
                          Professional Information
                        </h5>
                        <p
                          style={{
                            margin: '2px 0 0 0',
                            color: 'rgba(255, 255, 255, 0.9)',
                            fontSize: '12px',
                            fontFamily: 'Inter, system-ui, sans-serif',
                          }}
                        >
                          Teacher professional and academic details
                        </p>
                      </div>
                    </div>
                    <button
                      onClick={() => setShowProfessionalInfo(!showProfessionalInfo)}
                      style={{
                        width: '36px',
                        height: '36px',
                        borderRadius: '50%',
                        backgroundColor: 'rgba(255, 255, 255, 0.2)',
                        border: 'none',
                        color: '#ffffff',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        cursor: 'pointer',
                        transition: 'all 0.2s ease',
                        flexShrink: 0,
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.backgroundColor = 'rgba(255, 255, 255, 0.3)'
                        e.currentTarget.style.transform = 'scale(1.1)'
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.backgroundColor = 'rgba(255, 255, 255, 0.2)'
                        e.currentTarget.style.transform = 'scale(1)'
                      }}
                      title={showProfessionalInfo ? "Hide Section" : "Show Section"}
                    >
                      {showProfessionalInfo ? <BsChevronUp size={18} /> : <BsChevronDown size={18} />}
                    </button>
                  </div>
                </div>

                {showProfessionalInfo && (
                  <Card.Body style={{ padding: '20px' }}>
                    <Row className="g-3 text-start">
                      <Col xs={12} md={6}>
                        <div
                          style={{
                            backgroundColor: '#F9FAFB',
                            borderRadius: '12px',
                            padding: '14px',
                            border: '1px solid #E5E7EB',
                          }}
                        >
                          <div
                            style={{
                              backgroundColor: '#ffffff',
                              borderRadius: '8px',
                              padding: '12px',
                              border: '1px solid #E5E7EB',
                            }}
                          >
                            <div className="d-flex align-items-center gap-2 mb-1">
                              <div
                                style={{
                                  width: '36px',
                                  height: '36px',
                                  borderRadius: '8px',
                                  backgroundColor: '#D1FAE5',
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                }}
                              >
                                <BsBuilding size={16} style={{ color: '#10B981' }} />
                              </div>
                              <div style={{ flex: 1 }}>
                                <div className="info-label" style={{ marginBottom: '2px', fontSize: '11px' }}>Institute Name</div>
                                <div className="info-value" style={{ fontSize: '15px', fontWeight: '700', color: '#111827' }}>
                                  {instituteName}
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                      </Col>
                      <Col xs={12} md={6}>
                        <div
                          style={{
                            backgroundColor: '#F9FAFB',
                            borderRadius: '12px',
                            padding: '14px',
                            border: '1px solid #E5E7EB',
                          }}
                        >
                          <div
                            style={{
                              backgroundColor: '#ffffff',
                              borderRadius: '8px',
                              padding: '12px',
                              border: '1px solid #E5E7EB',
                            }}
                          >
                            <div className="d-flex align-items-center gap-2 mb-1">
                              <div
                                style={{
                                  width: '36px',
                                  height: '36px',
                                  borderRadius: '8px',
                                  backgroundColor: '#D1FAE5',
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                }}
                              >
                                <BsPersonBadge size={16} style={{ color: '#10B981' }} />
                              </div>
                              <div style={{ flex: 1 }}>
                                <div className="info-label" style={{ marginBottom: '2px', fontSize: '11px' }}>Qualification</div>
                                <div className="info-value" style={{ fontSize: '15px', fontWeight: '700', color: '#111827' }}>
                                  {teacher?.qualification || 'N/A'}
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                      </Col>
                      <Col xs={12} md={6}>
                        <div
                          style={{
                            backgroundColor: '#F9FAFB',
                            borderRadius: '12px',
                            padding: '14px',
                            border: '1px solid #E5E7EB',
                          }}
                        >
                          <div
                            style={{
                              backgroundColor: '#ffffff',
                              borderRadius: '8px',
                              padding: '12px',
                              border: '1px solid #E5E7EB',
                            }}
                          >
                            <div className="d-flex align-items-center gap-2 mb-1">
                              <div
                                style={{
                                  width: '36px',
                                  height: '36px',
                                  borderRadius: '8px',
                                  backgroundColor: '#D1FAE5',
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                }}
                              >
                                <BsCalendar3 size={16} style={{ color: '#10B981' }} />
                              </div>
                              <div style={{ flex: 1 }}>
                                <div className="info-label" style={{ marginBottom: '2px', fontSize: '11px' }}>Joining Date</div>
                                <div className="info-value" style={{ fontSize: '15px', fontWeight: '700', color: '#111827' }}>
                                  {teacher?.joining_date || 'N/A'}
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                      </Col>
                      <Col xs={12} md={6}>
                        <div
                          style={{
                            backgroundColor: '#F9FAFB',
                            borderRadius: '12px',
                            padding: '14px',
                            border: '1px solid #E5E7EB',
                          }}
                        >
                          <div
                            style={{
                              backgroundColor: '#ffffff',
                              borderRadius: '8px',
                              padding: '12px',
                              border: '1px solid #E5E7EB',
                            }}
                          >
                            <div className="d-flex align-items-center gap-2 mb-1">
                              <div
                                style={{
                                  width: '36px',
                                  height: '36px',
                                  borderRadius: '8px',
                                  backgroundColor: teacher?.status === 'active' ? '#D1FAE5' : '#FEE2E2',
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                }}
                              >
                                <BsPersonBadge size={16} style={{ color: teacher?.status === 'active' ? '#10B981' : '#EF4444' }} />
                              </div>
                              <div style={{ flex: 1 }}>
                                <div className="info-label" style={{ marginBottom: '2px', fontSize: '11px' }}>Status</div>
                                <div className="info-value" style={{ fontSize: '15px', fontWeight: '700', color: teacher?.status === 'active' ? '#10B981' : '#EF4444' }}>
                                  {teacher?.status
                                    ? teacher.status.charAt(0).toUpperCase() +
                                      teacher.status.slice(1)
                                    : 'N/A'}
                                </div>
                              </div>
                            </div>
                          </div>
                      </div>
                    </Col>
                </Row>
              </Card.Body>
                )}
            </Card>
            </Col>
          </Row>
          </Container>
      </div>
        </>
  )
}

export default TeacherProfile
